/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Hook to fetch a box's settings (features + timezone + rooms) from the SAME box doc read.
 *
 * One getBox read serves both the feature gates and the schedule-template
 * target-week anchor, so callers that need either don't fan out two reads on the
 * same document. Returns disabled features + null timezone until the read
 * resolves (opt-in / fail-safe).
 *
 * Target semantics: `undefined` falls back to the user's own boxID (legacy
 * callers that just want "my box"); an EXPLICIT `null` keeps the hook closed
 * with no read at all — that's how callers gate the fetch to when a surface
 * actually needs it (e.g. only while a dialog is open) without paying a box-doc
 * read on every page load.
 *
 * `isLoading` is DERIVED from whether the stored result is for the CURRENT target
 * box — so it reads `true` on the very first render after the target changes (e.g.
 * a dialog opening flips boxID null→real), with no stale-false window. Callers gate
 * trust-critical work (the box-local Sunday anchor) on it: applying with a
 * not-yet-resolved timezone would fall back to noon UTC and be server-rejected for
 * UTC+13/+14 boxes.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getBox } from "@lemonadetraining/web-shared";
import { normalizeBoxRooms } from "@lemonadetraining/contracts";
import type { BoxFeatures, BoxRoom } from "@lemonadetraining/contracts";

const DISABLED_FEATURES: BoxFeatures = {
  bookingEnabled: false,
  programsEnabled: false,
  coachingEnabled: false,
};

interface UseBoxSettingsResult {
  features: BoxFeatures | null;
  timezone: string | null;
  /** undefined while loading, null when the box has none (or the read failed). */
  zone: string | null | undefined;
  rooms: BoxRoom[];
  isLoading: boolean;
  error: Error | null;
}

interface BoxSettingsState {
  /** The boxID this result is FOR (null before any fetch). */
  boxID: string | null;
  features: BoxFeatures | null;
  timezone: string | null;
  rooms: BoxRoom[];
  error: Error | null;
}

export const useBoxSettings = (boxID: string | null | undefined): UseBoxSettingsResult => {
  const userInfoData = useSelector((state: any) => state.user.userInfoData);
  // null = explicitly closed (no read); undefined/"" = fall back to own box.
  const targetBoxID: string | null = boxID === null ? null : boxID || userInfoData?.boxID || null;

  const [state, setState] = useState<BoxSettingsState>({
    boxID: null,
    features: null,
    timezone: null,
    rooms: [],
    error: null,
  });

  useEffect(() => {
    if (!targetBoxID) {
      setState({ boxID: null, features: null, timezone: null, rooms: [], error: null });
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const box = await getBox(targetBoxID);
        if (isMounted) {
          setState({
            boxID: targetBoxID,
            features: box?.settings?.features ?? DISABLED_FEATURES,
            timezone: box?.settings?.timezone ?? null,
            rooms: normalizeBoxRooms(box?.rooms),
            error: null,
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching box settings:", err);
          // Fail safe: features disabled, timezone null (the apply anchor then falls
          // back to noon UTC — which the server fails closed on for a tz-less box).
          setState({
            boxID: targetBoxID,
            features: DISABLED_FEATURES,
            timezone: null,
            rooms: [],
            error: err instanceof Error ? err : new Error("Failed to fetch box settings"),
          });
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [targetBoxID]);

  // Loading whenever we have a target but the stored result isn't for it yet —
  // true on the first render after the target changes, with no stale-false gap.
  const isLoading = Boolean(targetBoxID) && state.boxID !== targetBoxID;

  return {
    features: targetBoxID ? state.features : null,
    timezone: targetBoxID ? state.timezone : null,
    zone: !targetBoxID ? null : isLoading ? undefined : state.timezone,
    rooms: targetBoxID ? state.rooms : [],
    isLoading,
    error: state.error,
  };
};
